/**
 * 플랜·워크스페이스 활동.
 * 확정된 결과만 모은다. 작업 등록, 상태·심각도, 댓글·답글, 사진·첨부, 문서의 만들기·공개·휴지통,
 * 주간 보고의 만들기·마감, 기준선 저장. 본문 수정, 댓글 수정·삭제, 초안을 고친 과정은 넣지 않는다.
 */
import type {
  AttachmentLink,
  Baseline,
  ExecutionStatus,
  ExecutionTask,
  PlanDocument,
  Project,
  ProjectRecord,
  RecordKind,
  RecordSeverity,
  RecordStatus,
  TaskActivity,
  TaskComment,
  WeeklyReportDraft,
  WeeklyReportSnapshot,
} from "@/engine/types";
import { formatDayHeading } from "@/lib/date-format";
import { STATUS_LABEL } from "@/store/execution-task-ops";
import { RECORD_KIND_LABEL, RECORD_STATUSES, recordStatusLabel, SEVERITY_LABEL } from "@/store/record-ops";

export const ACTIVITY_PAGE_DAYS = 7;
const COLLAPSE_MS = 15 * 60 * 1000;
const IMAGE_EXT = /\.(png|jpe?g|gif|webp|svg|bmp|avif)$/i;

export const ACTIVITY_KIND_ORDER = ["task", "issue", "schedule", "document", "baseline"] as const;
export type ActivityKind = (typeof ACTIVITY_KIND_ORDER)[number];

export const ALL_ACTIVITY_KINDS: ActivityKind[] = [...ACTIVITY_KIND_ORDER];

/** 기본으로 모든 종류의 활동(작업·이슈·일정·문서·기준선)을 보여준다. */
export const DEFAULT_ACTIVITY_KINDS: ActivityKind[] = [...ACTIVITY_KIND_ORDER];

export const ACTIVITY_KIND_LABEL: Record<ActivityKind, string> = {
  task: "작업",
  issue: "이슈",
  schedule: "일정",
  document: "문서",
  baseline: "기준선",
};

/** 상태·심각도만 표식 칩으로 그린다. 문장으로 남는 값은 붙이지 않는다. */
export type ActivityValue =
  | { type: "taskStatus"; status: ExecutionStatus }
  | { type: "recordStatus"; recordKind: RecordKind; status: RecordStatus }
  | { type: "severity"; severity: RecordSeverity };

export type ActivityTarget =
  | { type: "task"; taskId: string; commentId?: string }
  | { type: "record"; recordId: string; commentId?: string }
  | { type: "document"; documentId: string }
  | { type: "weekly"; statusDate: string }
  | { type: "baseline"; baselineId: string };

export type ActivityEvent = {
  id: string;
  at: string;
  actor: string;
  planId: string;
  planName: string;
  kind: ActivityKind;
  subject: string;
  /** 대상 이름 바로 뒤에 붙는 말. 앞 공백이나 조사로 시작한다. */
  verb: string;
  /** 상태·심각도를 칩으로 그릴 때. 없으면 동사 문장만 보인다. */
  value?: ActivityValue;
  delayed: boolean;
  target: ActivityTarget;
};

type DraftEvent = ActivityEvent & { collapse?: string; burstAt: number };

export type ActivityGroup = { key: string; heading: string; events: ActivityEvent[] };

export function objectParticle(word: string): "을" | "를" {
  const syllable = word.charCodeAt(word.length - 1) - 0xac00;
  if (syllable < 0 || syllable > 11171) return "을";
  return syllable % 28 === 0 ? "를" : "을";
}

export function directionParticle(word: string): "로" | "으로" {
  const syllable = word.charCodeAt(word.length - 1) - 0xac00;
  if (syllable < 0 || syllable > 11171) return "로";
  const batchim = syllable % 28;
  return batchim === 0 || batchim === 8 ? "로" : "으로";
}

function named(value: string, fallback: string): string {
  const text = value.trim();
  if (!text || text === "—" || text === "현재") return fallback;
  return text;
}

function person(value: string | undefined): string {
  const text = value?.trim() ?? "";
  return text === "—" ? "" : text;
}

function isImageAttachment(file: AttachmentLink): boolean {
  return file.kind === "image" || !!file.mimeType?.startsWith("image/") || IMAGE_EXT.test(file.title);
}

function isDelayedText(value: string): boolean {
  return /지연|늦|막힘/.test(value);
}

function normalizeLog(message: string): string {
  return message.replaceAll("만듦", "생성");
}

function isCreateLog(message: string): boolean {
  const text = normalizeLog(message);
  return text === "생성" || text === "개정안 생성" || text.endsWith("등록") || text === "문서를 만들었습니다";
}

export function timeOf(value: string): number {
  const time = Date.parse(value);
  return Number.isNaN(time) ? Number.NaN : time;
}

export function localDayKey(value: string): string {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
}

function push(out: DraftEvent[], event: Omit<DraftEvent, "burstAt" | "delayed"> & { delayed?: boolean; collapse?: string }): void {
  const at = timeOf(event.at);
  if (Number.isNaN(at)) return;
  out.push({ ...event, delayed: event.delayed ?? false, burstAt: at });
}

function fileVerb(file: AttachmentLink): string {
  return isImageAttachment(file) ? "에 사진을 올렸습니다" : "에 첨부를 추가했습니다";
}

function commentVerb(comment: TaskComment): { verb: string; delayed: boolean } {
  const files = comment.attachments ?? [];
  const photos = files.filter(isImageAttachment).length;
  const others = files.length - photos;
  const body = comment.body.trim();
  const reply = !!comment.parentId;
  let verb: string;
  if (!body && photos > 0 && others === 0) verb = "에 사진을 올렸습니다";
  else if (!body && others > 0 && photos === 0) verb = "에 첨부를 추가했습니다";
  else verb = reply ? "에 답글을 남겼습니다" : "에 댓글을 남겼습니다";
  if (body && photos > 0) verb += ` · 사진 ${photos}장`;
  else if (body && others > 0) verb += ` · 첨부 ${others}개`;
  return { verb, delayed: body.length > 0 && isDelayedText(body) };
}

function collapseKey(event: { planId: string; kind: ActivityKind; targetId: string; field: string; actor: string }): string | undefined {
  if (event.field !== "status" && event.field !== "severity" && event.field !== "visibility") return undefined;
  return `${event.planId}:${event.kind}:${event.targetId}:${event.field}:${event.actor}`;
}

function matched(match: RegExpExecArray | null): string | null {
  const value = match?.[1];
  return value ? value : null;
}

type Spoken = { verb: string; field: string; delayed?: boolean; value?: ActivityValue };

function taskStatusOf(label: string): ExecutionStatus | undefined {
  if (label === "진행") return "inProgress";
  for (const status of Object.keys(STATUS_LABEL) as ExecutionStatus[]) {
    if (STATUS_LABEL[status] === label) return status;
  }
  return undefined;
}

function recordStatusOf(kind: RecordKind, label: string): RecordStatus | undefined {
  return RECORD_STATUSES[kind].find((status) => recordStatusLabel(kind, status) === label);
}

function severityOf(label: string): RecordSeverity | undefined {
  for (const severity of Object.keys(SEVERITY_LABEL) as RecordSeverity[]) {
    if (SEVERITY_LABEL[severity] === label) return severity;
  }
  return undefined;
}

function taskVerb(message: string): Spoken | null {
  const text = normalizeLog(message);
  if (text === "생성") return { verb: " 작업을 등록했습니다", field: "create" };
  const status = matched(/^상태 → (.+)$/.exec(text));
  if (status) {
    const mapped = taskStatusOf(status);
    return {
      verb: ` 상태를 ${status}${directionParticle(status)} 바꿨습니다`,
      field: "status",
      delayed: isDelayedText(status),
      value: mapped ? { type: "taskStatus", status: mapped } : undefined,
    };
  }
  const file = matched(/^첨부 추가: (.+)$/.exec(text));
  if (file) return { verb: IMAGE_EXT.test(file) ? "에 사진을 올렸습니다" : "에 첨부를 추가했습니다", field: `file:${file}` };
  return null;
}

function recordVerb(record: ProjectRecord, message: string): Spoken | null {
  const text = normalizeLog(message);
  if (text === "생성" || text === "개정안 생성" || text.endsWith("등록")) {
    const label = text === "개정안 생성" ? "개정안" : RECORD_KIND_LABEL[record.kind];
    return { verb: ` ${label}${objectParticle(label)} 등록했습니다`, field: "create" };
  }
  const status = matched(/^상태 → (.+)$/.exec(text));
  if (status) {
    const mapped = recordStatusOf(record.kind, status);
    return {
      verb: ` 상태를 ${status}${directionParticle(status)} 바꿨습니다`,
      field: "status",
      delayed: isDelayedText(status),
      value: mapped ? { type: "recordStatus", recordKind: record.kind, status: mapped } : undefined,
    };
  }
  const severity = matched(/^심각도 → (.+)$/.exec(text));
  if (severity) {
    const mapped = severityOf(severity);
    return {
      verb: ` 심각도를 ${severity}${directionParticle(severity)} 바꿨습니다`,
      field: "severity",
      value: mapped ? { type: "severity", severity: mapped } : undefined,
    };
  }
  if (text.endsWith("대체됨")) return { verb: ` ${text.replace(/됨$/, "되었습니다")}`, field: "supersede" };
  const file = matched(/^첨부 추가: (.+)$/.exec(text));
  if (file) return { verb: IMAGE_EXT.test(file) ? "에 사진을 올렸습니다" : "에 첨부를 추가했습니다", field: `file:${file}` };
  return null;
}

function documentVerb(message: string): { verb: string; field: string } | null {
  switch (message) {
    case "문서를 만들었습니다":
      return { verb: " 문서를 만들었습니다", field: "create" };
    case "공개로 바꿨습니다":
      return { verb: " 공개로 바꿨습니다", field: "visibility" };
    case "비공개로 바꿨습니다":
      return { verb: " 비공개로 바꿨습니다", field: "visibility" };
    case "휴지통으로 옮겼습니다":
      return { verb: " 휴지통으로 옮겼습니다", field: "trash" };
    case "복원했습니다":
      return { verb: " 복원했습니다", field: "restore" };
    default:
      return null;
  }
}

function attachmentCovered(message: string, files: AttachmentLink[]): boolean {
  const name = matched(/^첨부 추가: (.+)$/.exec(normalizeLog(message)));
  if (!name) return false;
  return files.some((file) => file.title === name || file.url === name);
}

function addFiles(
  out: DraftEvent[],
  input: { planId: string; planName: string; kind: ActivityKind; subject: string; target: ActivityTarget; targetId: string; files: AttachmentLink[] },
): void {
  for (const file of input.files) {
    push(out, {
      id: `file:${input.planId}:${input.targetId}:${file.id}`,
      at: file.addedAt,
      actor: person(file.addedBy),
      planId: input.planId,
      planName: input.planName,
      kind: input.kind,
      subject: input.subject,
      verb: fileVerb(file),
      target: input.target,
    });
  }
}

function addComments(
  out: DraftEvent[],
  input: { planId: string; planName: string; kind: ActivityKind; subject: string; targetOf: (commentId: string) => ActivityTarget; comments: TaskComment[] },
): void {
  for (const comment of input.comments) {
    if (comment.deleted) continue;
    const spoken = commentVerb(comment);
    push(out, {
      id: `comment:${input.planId}:${comment.id}`,
      at: comment.at,
      actor: comment.author,
      planId: input.planId,
      planName: input.planName,
      kind: input.kind,
      subject: input.subject,
      verb: spoken.verb,
      delayed: spoken.delayed,
      target: input.targetOf(comment.id),
    });
  }
}

function addLogged(
  out: DraftEvent[],
  input: {
    planId: string;
    planName: string;
    kind: ActivityKind;
    subject: string;
    target: ActivityTarget;
    targetId: string;
    logs: TaskActivity[];
    files: AttachmentLink[];
    verbOf: (message: string) => Spoken | null;
  },
): void {
  for (const log of input.logs) {
    if (attachmentCovered(log.message, input.files)) continue;
    const spoken = input.verbOf(log.message);
    if (!spoken) continue;
    push(out, {
      id: `log:${input.planId}:${input.targetId}:${log.id}`,
      at: log.at,
      actor: person(log.actor),
      planId: input.planId,
      planName: input.planName,
      kind: input.kind,
      subject: input.subject,
      verb: spoken.verb,
      value: spoken.value,
      delayed: spoken.delayed,
      collapse: collapseKey({ planId: input.planId, kind: input.kind, targetId: input.targetId, field: spoken.field, actor: log.actor }),
      target: input.target,
    });
  }
}

function addTasks(out: DraftEvent[], project: Project): void {
  for (const task of project.executionTasks) addTask(out, project, task);
}

function addTask(out: DraftEvent[], project: Project, task: ExecutionTask): void {
  const subject = named(task.title, "제목 없음");
  const target: ActivityTarget = { type: "task", taskId: task.id };
  const created = task.activity.some((log) => isCreateLog(log.message));
  addLogged(out, {
    planId: project.id,
    planName: project.name,
    kind: "task",
    subject,
    target,
    targetId: task.id,
    logs: task.activity,
    files: task.attachments,
    verbOf: taskVerb,
  });
  if (!created) {
    push(out, {
      id: `create:${project.id}:${task.id}`,
      at: task.createdAt,
      actor: "",
      planId: project.id,
      planName: project.name,
      kind: "task",
      subject,
      verb: " 작업을 등록했습니다",
      target,
    });
  }
  addFiles(out, { planId: project.id, planName: project.name, kind: "task", subject, target, targetId: task.id, files: task.attachments });
  addComments(out, {
    planId: project.id,
    planName: project.name,
    kind: "task",
    subject,
    comments: task.comments,
    targetOf: (commentId) => ({ type: "task", taskId: task.id, commentId }),
  });
}

function addRecords(out: DraftEvent[], project: Project): void {
  for (const record of project.records) addRecord(out, project, record);
}

function addRecord(out: DraftEvent[], project: Project, record: ProjectRecord): void {
  const subject = named(record.title, RECORD_KIND_LABEL[record.kind]);
  const target: ActivityTarget = { type: "record", recordId: record.id };
  const created = record.activity.some((log) => isCreateLog(log.message));
  addLogged(out, {
    planId: project.id,
    planName: project.name,
    kind: "issue",
    subject,
    target,
    targetId: record.id,
    logs: record.activity,
    files: record.attachments,
    verbOf: (message) => recordVerb(record, message),
  });
  if (!created) {
    const label = RECORD_KIND_LABEL[record.kind];
    push(out, {
      id: `create:${project.id}:${record.id}`,
      at: record.createdAt,
      actor: person(record.author),
      planId: project.id,
      planName: project.name,
      kind: "issue",
      subject,
      verb: ` ${label}${objectParticle(label)} 등록했습니다`,
      target,
    });
  }
  addFiles(out, { planId: project.id, planName: project.name, kind: "issue", subject, target, targetId: record.id, files: record.attachments });
  addComments(out, {
    planId: project.id,
    planName: project.name,
    kind: "issue",
    subject,
    comments: record.comments,
    targetOf: (commentId) => ({ type: "record", recordId: record.id, commentId }),
  });
}

function addDocuments(out: DraftEvent[], project: Project): void {
  for (const doc of project.documents) addDocument(out, project, doc);
}

function addDocument(out: DraftEvent[], project: Project, doc: PlanDocument): void {
  const subject = named(doc.title, "제목 없음");
  const target: ActivityTarget = { type: "document", documentId: doc.id };
  const created = doc.activity.some((log) => isCreateLog(log.message));
  addLogged(out, {
    planId: project.id,
    planName: project.name,
    kind: "document",
    subject,
    target,
    targetId: doc.id,
    logs: doc.activity,
    files: doc.attachments,
    verbOf: documentVerb,
  });
  if (!created) {
    push(out, {
      id: `create:${project.id}:${doc.id}`,
      at: doc.createdAt,
      actor: doc.authorId,
      planId: project.id,
      planName: project.name,
      kind: "document",
      subject,
      verb: " 문서를 만들었습니다",
      target,
    });
  }
  addFiles(out, { planId: project.id, planName: project.name, kind: "document", subject, target, targetId: doc.id, files: doc.attachments });
  addComments(out, {
    planId: project.id,
    planName: project.name,
    kind: "document",
    subject,
    comments: doc.comments,
    targetOf: () => target,
  });
}

function addWeekly(out: DraftEvent[], project: Project): void {
  for (const draft of Object.values(project.weeklyDrafts ?? {})) addDraft(out, project, draft);
  for (const snapshot of project.weeklySnapshots ?? []) addSnapshot(out, project, snapshot);
}

function weeklyVerb(subject: string, action: "만들었습니다" | "마감했습니다"): string {
  if (subject.includes("주간 보고")) return `${objectParticle(subject)} ${action}`;
  return ` 주간 보고를 ${action}`;
}

function addDraft(out: DraftEvent[], project: Project, draft: WeeklyReportDraft): void {
  if (draft.isRevision) return;
  const subject = named(draft.weekLabel, "") || named(draft.title, "주간 보고");
  push(out, {
    id: `weekly-draft:${project.id}:${draft.id}`,
    at: draft.createdAt,
    actor: person(draft.author),
    planId: project.id,
    planName: project.name,
    kind: "schedule",
    subject,
    verb: weeklyVerb(subject, "만들었습니다"),
    target: { type: "weekly", statusDate: draft.statusDate },
  });
}

function addSnapshot(out: DraftEvent[], project: Project, snapshot: WeeklyReportSnapshot): void {
  const report = snapshot.document.report;
  const fromReport = typeof report.weekLabel === "string" ? report.weekLabel : "";
  const author = typeof report.author === "string" ? report.author : "";
  const subject = named(fromReport, "") || named(snapshot.periodLabel, "주간 보고");
  push(out, {
    id: `weekly-close:${project.id}:${snapshot.id}`,
    at: snapshot.closedAt,
    actor: person(author),
    planId: project.id,
    planName: project.name,
    kind: "schedule",
    subject,
    verb: weeklyVerb(subject, "마감했습니다"),
    target: { type: "weekly", statusDate: snapshot.statusDate },
  });
}

function addBaselines(out: DraftEvent[], project: Project): void {
  for (const baseline of project.baselines) addBaseline(out, project, baseline);
}

function addBaseline(out: DraftEvent[], project: Project, baseline: Baseline): void {
  const subject = named(baseline.name, "기준선");
  push(out, {
    id: `baseline:${project.id}:${baseline.id}`,
    at: baseline.createdAt,
    actor: "",
    planId: project.id,
    planName: project.name,
    kind: "baseline",
    subject,
    verb: `${objectParticle(subject)} 저장했습니다`,
    target: { type: "baseline", baselineId: baseline.id },
  });
}

function collapseDrafts(events: DraftEvent[]): ActivityEvent[] {
  const kept: DraftEvent[] = [];
  const indexByKey = new Map<string, number>();
  const ordered = [...events].sort((a, b) => a.burstAt - b.burstAt || a.id.localeCompare(b.id));
  for (const event of ordered) {
    if (event.collapse) {
      const index = indexByKey.get(event.collapse);
      const prev = index != null ? kept[index] : undefined;
      if (prev && event.burstAt - prev.burstAt <= COLLAPSE_MS) {
        kept[index!] = { ...event, burstAt: prev.burstAt };
        continue;
      }
      indexByKey.set(event.collapse, kept.length);
    }
    kept.push(event);
  }
  kept.sort((a, b) => timeOf(b.at) - timeOf(a.at) || b.id.localeCompare(a.id));
  return kept.map(({ collapse: _collapse, burstAt: _burstAt, ...event }) => event);
}

export function collectActivity(projects: readonly Project[]): ActivityEvent[] {
  const drafts: DraftEvent[] = [];
  for (const project of projects) {
    if (project.lifecycle === "trashed") continue;
    addTasks(drafts, project);
    addRecords(drafts, project);
    addDocuments(drafts, project);
    addWeekly(drafts, project);
    addBaselines(drafts, project);
  }
  return collapseDrafts(drafts);
}

export function sameActivityKinds(left: readonly ActivityKind[], right: readonly ActivityKind[]): boolean {
  if (left.length !== right.length) return false;
  const have = new Set(left);
  return right.every((kind) => have.has(kind));
}

export function filterActivity(
  events: readonly ActivityEvent[],
  input: { kinds: readonly ActivityKind[]; actors: readonly string[]; planIds: readonly string[]; query: string; delayedOnly?: boolean },
): ActivityEvent[] {
  const kinds = new Set(input.kinds);
  const actors = new Set(input.actors);
  const plans = new Set(input.planIds);
  const query = input.query.trim().toLowerCase();
  return events.filter((event) => {
    if (input.delayedOnly && !event.delayed) return false;
    if (!kinds.has(event.kind)) return false;
    if (actors.size > 0 && !actors.has(event.actor)) return false;
    if (plans.size > 0 && !plans.has(event.planId)) return false;
    if (!query) return true;
    const haystack = `${event.actor} ${event.subject} ${event.verb} ${event.planName}`.toLowerCase();
    return haystack.includes(query);
  });
}

export function activityActors(events: readonly ActivityEvent[]): string[] {
  return [...new Set(events.map((event) => event.actor).filter(Boolean))].sort((a, b) => a.localeCompare(b, "ko"));
}

/** 오늘이 있으면 오늘 건수. 없으면 최근 7일. 지연이 없으면 그 말은 뺀다. */
export function activityScale(events: readonly ActivityEvent[], now = new Date()): string | undefined {
  const todayKey = localDayKey(now.toISOString());
  const today = events.filter((event) => localDayKey(event.at) === todayKey);
  const delayedOf = (list: readonly ActivityEvent[]) => list.filter((event) => event.delayed).length;
  if (today.length > 0) {
    const delayed = delayedOf(today);
    return delayed > 0 ? `오늘 ${today.length}건 · 지연 ${delayed}건` : `오늘 ${today.length}건`;
  }
  const start = activityWindowStart(now, 1);
  const recent = events.filter((event) => timeOf(event.at) >= start);
  if (recent.length === 0) return undefined;
  const delayed = delayedOf(recent);
  return delayed > 0 ? `최근 ${recent.length}건 · 지연 ${delayed}건` : `최근 ${recent.length}건`;
}

export function activityWindowStart(now: Date, pages: number): number {
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  start.setDate(start.getDate() - Math.max(1, pages) * ACTIVITY_PAGE_DAYS + 1);
  return start.getTime();
}

export function sliceActivity(events: readonly ActivityEvent[], now: Date, pages: number): { shown: ActivityEvent[]; hasOlder: boolean } {
  const start = activityWindowStart(now, pages);
  const shown: ActivityEvent[] = [];
  let hasOlder = false;
  for (const event of events) {
    if (timeOf(event.at) >= start) shown.push(event);
    else hasOlder = true;
  }
  return { shown, hasOlder };
}

export function groupActivity(events: readonly ActivityEvent[], now = new Date()): ActivityGroup[] {
  const groups: ActivityGroup[] = [];
  for (const event of events) {
    const key = localDayKey(event.at);
    const last = groups[groups.length - 1];
    if (!last || last.key !== key) groups.push({ key, heading: formatDayHeading(event.at, { now }), events: [event] });
    else last.events.push(event);
  }
  return groups;
}
